import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import useLoginCheck from "../util/useLoginCheck";
import AutocompleteButton from "../util/AutocompleteButton";
import EditRecipeIngredientItem from "./EditRecipeIngredientItem";
import Spinner from "../util/Spinner";
import { updateRecipe } from "../slice/recipeSlice";
import { fetchMeasurements } from "../slice/measurementSlice";
import { fetchIngredients } from "../slice/ingredientsSlice";

const EditRecipe = ({ recipe: initialRecipe, closePopup, refreshRecipe }) => {
  const dispatch = useDispatch();
  const { measurements, isLoadingMeasurements } = useSelector((state) => {
    return state?.measurements;
  });
  const { ingredients, isLoadingIngredients } = useSelector((state) => {
    return state?.ingredients;
  });
  const navigate = useNavigate();
  const [editableRecipe, setEditableRecipe] = useState({
    sid: "",
    name: "",
    calories: 0,
    fromTheKitchenOf: "",
    totalTime: 0,
    instructions: "",
    recipeIngredients: [],
  });
  const [selectedIngredientSid, setSelectedIngredientSid] = useState("");
  const [savingRecipe, setSavingRecipe] = useState(false);

  useLoginCheck();

  useEffect(() => {
    console.log("fetching measurements and ingredients");
    dispatch(fetchMeasurements());
    dispatch(fetchIngredients());
  }, [dispatch]);

  useEffect(() => {
    if (initialRecipe) {
      console.log("setting reicpe");
      setEditableRecipe(initialRecipe);
    }
  }, [initialRecipe]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSavingRecipe(true);
      console.log("updating recipe...");
      const postData = {
        sid: editableRecipe?.sid,
        name: editableRecipe?.name,
        calories: editableRecipe?.calories,
        fromTheKitchenOf: editableRecipe?.fromTheKitchenOf,
        instructions: editableRecipe?.instructions,
        totalTime: editableRecipe?.totalTime,
        recipeIngredients: editableRecipe?.recipeIngredients,
      };
      const resultAction = await dispatch(updateRecipe(postData));
      if (updateRecipe.fulfilled.match(resultAction)) {
        console.log("successfully updated recipe!");
        refreshRecipe?.();
        navigate(`/recipes/${editableRecipe?.sid}?refresh=true`);
        setSavingRecipe(false);
        closePopup();
      }
    } catch (error) {
      console.log("error updating recipe, message: ", error.message);
    } finally {
      setSavingRecipe(false);
    }
  };

  const removeRecipeIngredient = (recipeIngredient) => {
    console.log("removing recipe ingredient from list: ", recipeIngredient);
    setEditableRecipe((prev) => ({
      ...prev,
      recipeIngredients: prev.recipeIngredients.filter(
        (ri) => ri?.ingredientSid !== recipeIngredient?.ingredientSid
      ),
    }));
  };

  const updateMeasurement = (recipeIngredient, measurementSid) => {
    console.log(
      "EditRecipe updating " +
        recipeIngredient?.ingredientSid +
        " measurement to " +
        measurementSid
    );
    setEditableRecipe((prev) => ({
      ...prev,
      recipeIngredients: prev.recipeIngredients.map((ingredient) =>
        ingredient.ingredientSid === recipeIngredient.ingredientSid
          ? {
              ...ingredient,
              measurementSid,
            }
          : ingredient
      ),
    }));
  };

  const updateQuantity = (recipeIngredient, quantity) => {
    console.log(
      "EditRecipe updating " +
        recipeIngredient?.ingredientSid +
        " quantity to " +
        quantity
    );
    setEditableRecipe((prev) => ({
      ...prev,
      recipeIngredients: prev.recipeIngredients.map((ingredient) =>
        ingredient.ingredientSid === recipeIngredient.ingredientSid
          ? {
              ...ingredient,
              quantity,
            }
          : ingredient
      ),
    }));
  };

  const addRecipeIngredient = () => {
    console.log("addRecipeIngredient invoked with id ", selectedIngredientSid);
    const hasRecipeIngredient = editableRecipe?.recipeIngredients.some(
      (ri) => ri.ingredientSid === selectedIngredientSid
    );
    if (!hasRecipeIngredient) {
      const ingredient = ingredients.find(
        (item) => item?.sid === selectedIngredientSid
      );
      console.log("Adding recipe ingredient to list ", ingredient);
      setEditableRecipe((prev) => ({
        ...prev,
        recipeIngredients: [
          ...prev.recipeIngredients,
          {
            id: null,
            ingredient: {
              sid: ingredient?.sid,
              ingredientType: {
                sid: ingredient?.ingredientType?.sid,
                description: ingredient?.ingredientType?.description,
                isMeasured: ingredient?.ingredientType?.isMeasured,
              },
              ingredientTypeSid: ingredient?.ingredientType?.sid,
              name: ingredient?.name,
            },
            ingredientSid: ingredient?.sid,
            measurement: ingredient?.measurement
              ? {
                  sid: ingredient?.measurement.sid,
                  description: ingredient?.measurement?.description,
                }
              : null,
            measurementSid: ingredient?.measurementSid,
            quantity: 1,
          },
        ],
      }));
    } else {
      console.log(
        "Not adding recipe ingredient " +
          selectedIngredientSid +
          " to list--it's already there!"
      );
    }
    setSelectedIngredientSid("");
  };

  const handleChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    console.log(`EditRecipe handleChange { name: ${name}, value: ${value} }`);
    setEditableRecipe((prev) => ({ ...prev, [name]: value }));
  };

  const inputClasses =
    "w-full border border-stone-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-600";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      {isLoadingMeasurements && isLoadingIngredients ? (
        <Spinner />
      ) : (
        <div className="bg-white rounded-lg p-6 w-full sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <h2 className="text-lg font-semibold text-stone-800 mb-4">
            Update recipe
          </h2>
          <form onSubmit={handleSubmit} className="space-y-3">
            <input
              type="text"
              placeholder="Name"
              name="name"
              className={inputClasses}
              value={editableRecipe?.name ?? ""}
              onChange={handleChange}
              required
            ></input>
            <input
              type="text"
              placeholder="Calories"
              name="calories"
              className={inputClasses}
              value={editableRecipe?.calories}
              onChange={handleChange}
            ></input>
            <input
              type="text"
              placeholder="From the Kitchen Of"
              name="fromTheKitchenOf"
              className={inputClasses}
              value={editableRecipe?.fromTheKitchenOf}
              onChange={handleChange}
            ></input>
            <input
              type="text"
              placeholder="Total time"
              name="totalTime"
              className={inputClasses}
              value={editableRecipe?.totalTime}
              onChange={handleChange}
            ></input>
            <textarea
              type="text"
              placeholder="Instructions"
              name="instructions"
              className={`${inputClasses} min-h-[100px]`}
              value={editableRecipe?.instructions}
              onChange={handleChange}
            ></textarea>
            <hr className="border-stone-200" />
            <fieldset>
              <legend className="text-sm font-medium text-stone-500 mb-2">
                Add ingredients
              </legend>
              <AutocompleteButton
                items={ingredients}
                updateItems={(i) => setSelectedIngredientSid(i?.sid)}
                buttonHandler={addRecipeIngredient}
              />
            </fieldset>
            <fieldset>
              <ul className="space-y-2">
                {editableRecipe?.recipeIngredients?.map(
                  (recipeIngredient) => {
                    return (
                      <EditRecipeIngredientItem
                        key={recipeIngredient?.ingredientSid}
                        ingredient={recipeIngredient}
                        removeRecipeIngredient={removeRecipeIngredient}
                        measurements={measurements}
                        updateMeasurement={updateMeasurement}
                        updateQuantity={updateQuantity}
                      />
                    );
                  }
                )}
              </ul>
            </fieldset>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={closePopup} className="btn-secondary">
                Cancel
              </button>
              <button
                type="submit"
                disabled={savingRecipe}
                className="btn-primary"
              >
                {savingRecipe ? "Saving..." : "Save"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default EditRecipe;
